import { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Card } from 'ui-components/components/card';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';

import Global from '../../../../../../../../store/global-selectors';
import { LocaleContext } from '../../../../../../../../intl/locale-context-provider';
import { getLink } from '../../../../../../../../services/link-service';
import { linkIDs } from '../../../../../../../../services/link-service/links';
import { REFUND_ASSIST_ENABLE_TOGGLE } from '../../../../../../../../constants/analytics-events';
import { REFUND_ASSIST_TOGGLE_SOURCE } from '../../../../../../../../constants/refund-assist-source-constants';
import { SellerSettings } from '../../../../../../../../enums/seller-settings/seller-settings';
import { setRefundAssistStatus } from '../../../../../../../../pages/settings/refund-assist/api';
import { Segment } from '../../../../../../../../services/analytics-services/segment';
import { LogService } from '../../../../../../../../services/log-service';
import { saveNewSellerSettings } from '../../../../../../../../store/seller/seller.actions';
import { messages } from './messages';

import style from './refund-assist-section.module.less';

type RefundAssistSectionProps = {
  onSaveFunctionReady: (saveFunction: () => Promise<void>) => void;
  onToggleChange: (isEnabled: boolean) => void;
};

export const FTU_REFUND_ASSIST_ENABLED_KEY = 'ftu_refund_assist_enabled';

type ErrorWithResponse = {
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
  };
  message?: string;
};

const isErrorWithResponse = (error: unknown): error is ErrorWithResponse => {
  return typeof error === 'object' && error !== null;
};

const getInitialValue = () => {
  try {
    const storedValue = localStorage.getItem(FTU_REFUND_ASSIST_ENABLED_KEY);
    if (storedValue !== null) {
      return JSON.parse(storedValue);
    }
  } catch {
    // If localStorage fails, fall through to default
  }

  return true;
};

export const RefundAssistSection = ({ onSaveFunctionReady, onToggleChange }: RefundAssistSectionProps) => {
  const dispatch = useDispatch();

  const { locale } = useContext(LocaleContext);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);

  const [isRefundAssistEnabled, setIsRefundAssistEnabled] = useState(getInitialValue);
  const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(false);

  const kbLink = useMemo(() => getLink(linkIDs.Shipstation2021Terms, homeCountry, locale), [homeCountry, locale]);

  const handleToggle = useCallback(() => {
    setIsRefundAssistEnabled((prevValue: boolean) => {
      const newValue = !prevValue;
      onToggleChange(newValue);
      Segment.trackEvent(REFUND_ASSIST_ENABLE_TOGGLE, { isEnabled: newValue, source: REFUND_ASSIST_TOGGLE_SOURCE.FTU_WIZARD });

      return newValue;
    });
  }, [onToggleChange]);

  const handleDisclaimerToggle = useCallback(() => {
    setIsDisclaimerExpanded((prev) => !prev);
  }, []);

  const saveRefundAssist = useCallback(async () => {
    try {
      await setRefundAssistStatus(isRefundAssistEnabled);
      localStorage.setItem(FTU_REFUND_ASSIST_ENABLED_KEY, JSON.stringify(isRefundAssistEnabled));

      const refundAssistConfig = {
        [SellerSettings.isRefundAssistEnabled.key]: isRefundAssistEnabled,
      };
      dispatch(saveNewSellerSettings(refundAssistConfig));
    } catch (error: unknown) {
      const errorDetails = isErrorWithResponse(error)
        ? {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message,
          }
        : { message: String(error) };

      LogService.logError(new Error('Failed to update refund assist status in FTU wizard', { cause: error }), {
        isRefundAssistEnabled,
        ...errorDetails,
      });
    }
  }, [isRefundAssistEnabled, dispatch]);

  useEffect(() => {
    onSaveFunctionReady(saveRefundAssist);
  }, [onSaveFunctionReady, saveRefundAssist]);

  return (
    <FlexColumn className={style['refund-assist-section']} gap={Gap.Space16}>
      <FlexColumn gap={Gap.Space8}>
        <Text textType={TextType.H3} value={messages.sectionTitle} />
        <Text textType={TextType.Paragraph} value={messages.sectionDescription} />
      </FlexColumn>

      <FlexRow gap={Gap.Space8}>
        <Card className={style['refund-assist-card']}>
          <FlexColumn className={style['refund-assist-card-toggle']} gap={Gap.Space8}>
            <FlexRow className={style['toggle-row']}>
              <Text textType={TextType.H4} value={messages.toggleLabel} />
              <CheckboxSlider checkboxId='ftu-refund-assist-toggle' checked={isRefundAssistEnabled} onChange={handleToggle} />
            </FlexRow>
            <Text textType={TextType.Caption} value={messages.toggleCaption} />
            <Button className={style['terms-and-conditions']} href={kbLink} theme={THEME_CONSTANTS.link}>
              <Text textType={TextType.Paragraph} value={messages.termsAndConditions} />
              <FontAwesomeIcon className={style['disclaimer-arrow']} icon={faArrowUpRightFromSquare} />
            </Button>
          </FlexColumn>

          <FlexColumn className={style['disclaimer-wrapper']}>
            <FlexRow className={style['disclaimer-content']} gap={Gap.Space16}>
              <Text textType={TextType.Paragraph} value={messages.disclaimerParagraph1} />
              {isDisclaimerExpanded && isRefundAssistEnabled && (
                <>
                  <Text textType={TextType.Paragraph} value={messages.disclaimerParagraph2} />
                  <Text textType={TextType.Paragraph} value={messages.disclaimerParagraph3} />
                  <Text textType={TextType.Paragraph} value={messages.disclaimerParagraph4} />
                </>
              )}
            </FlexRow>

            {isRefundAssistEnabled && (
              <Button
                aria-expanded={isDisclaimerExpanded}
                className={style['disclaimer-toggle-button']}
                onClick={handleDisclaimerToggle}
                theme={THEME_CONSTANTS.link}
              >
                <Text
                  className={style['disclaimer-toggle-text']}
                  textType={TextType.Label}
                  value={isDisclaimerExpanded ? messages.readLess : messages.readMore}
                />
                <FontAwesomeIcon className={style['disclaimer-arrow']} icon={isDisclaimerExpanded ? faChevronUp : faChevronDown} />
              </Button>
            )}
          </FlexColumn>
        </Card>
      </FlexRow>
    </FlexColumn>
  );
};
